import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders } from './ProtectedRoute';
import { 
  Share2, 
  Linkedin, 
  Facebook, 
  Instagram, 
  Mail, 
  Calendar, 
  Copy, 
  Check,
  ExternalLink,
  Users,
  Building2,
  Sparkles,
  Wand2,
  Loader2,
  RefreshCw
} from 'lucide-react';

// Use custom domain for marketing materials (not the preview domain)
const MARKETING_DOMAIN = 'https://www.deallabce.com';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ShareCourseDialog({ open, onOpenChange, course, scheduledCourse }) {
  const [copiedField, setCopiedField] = useState(null);
  const [activeTab, setActiveTab] = useState('social');
  
  // AI Generation state
  const [generatingContent, setGeneratingContent] = useState({});
  const [aiContent, setAiContent] = useState({});
  const [customPrompt, setCustomPrompt] = useState('');

  if (!course) return null;

  // Course URL for sharing - always use custom domain for marketing
  const courseUrl = `${MARKETING_DOMAIN}/public/courses/${course.id}`;
  
  // Format scheduled date if available
  const scheduledDate = scheduledCourse 
    ? new Date(scheduledCourse.scheduled_date).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })
    : null;
  
  const scheduledTime = scheduledCourse?.scheduled_time || '6:00 PM';

  // Social media share content
  const socialContent = {
    linkedin: `🎓 Upcoming CE Course: ${course.title}

${course.description}

✅ ${course.credit_hours} CE Hours
✅ TREC Approved
✅ Expert Instructors

${scheduledDate ? `📅 ${scheduledDate} at ${scheduledTime}` : '📅 Multiple dates available'}

Register now: ${courseUrl}

#RealEstateCE #ContinuingEducation #TexasRealEstate #TREC #ProfessionalDevelopment`,

    facebook: `🏠 Attention Texas Real Estate Professionals! 

${course.title} is now available for registration!

${course.description}

📚 ${course.credit_hours} CE Hours
✓ TREC Approved Provider #9980
${scheduledDate ? `📅 ${scheduledDate} at ${scheduledTime}` : ''}

Don't miss this opportunity to earn your CE credits while learning valuable skills to grow your business.

Register here: ${courseUrl}`,

    instagram: `🎓 ${course.title}

${course.description}

✅ ${course.credit_hours} CE Hours
✅ TREC Approved
✅ Expert Instruction
${scheduledDate ? `📅 ${scheduledDate}` : ''}

Link in bio to register! 👆

#TexasRealEstate #RealEstateCE #TREC #ContinuingEducation #RealEstateAgent #TexasAgent #CECredits #ProfessionalDevelopment #RealEstateEducation #DealLabCE`
  };

  // Eventbrite template
  const eventbriteTemplate = {
    title: `${course.title} - ${course.credit_hours} CE Hours | DealLab CE`,
    description: `${course.description}

COURSE DETAILS:
• ${course.credit_hours} TREC-Approved CE Hours
• Category: ${course.category}
• Delivery: In-Person / Live Webinar
• Provider: DealLab CE (TREC Provider #9980)

WHAT YOU'LL LEARN:
${course.description}

WHO SHOULD ATTEND:
• Licensed Real Estate Agents
• Real Estate Brokers
• Sales Agents seeking CE Credits
• Anyone looking to enhance their real estate knowledge

REGISTRATION INCLUDES:
• Course Materials
• Certificate of Completion
• TREC Reporting (within 10 business days)
• Light Refreshments (in-person events)

QUESTIONS?
Email: info@deallabce.com
Phone: (281) 900-5687

Register now to secure your spot!`,
    organizer: 'DealLab CE - TREC Provider #9980',
    venue: scheduledCourse?.venue_name || 'TBD',
    address: scheduledCourse ? `${scheduledCourse.address_1}, ${scheduledCourse.city}, ${scheduledCourse.state} ${scheduledCourse.zip}` : '',
    date: scheduledDate || 'TBD',
    time: scheduledTime,
    tags: ['Real Estate', 'Continuing Education', 'TREC', 'Texas Real Estate', 'CE Credits', 'Professional Development']
  };

  // Email templates
  const emailTemplates = {
    agents: {
      subject: `Earn ${course.credit_hours} CE Hours: ${course.title}`,
      body: `Hi [Agent Name],

Are you looking for quality CE courses that actually help you close more deals?

We're excited to invite you to register for our upcoming course:

📚 ${course.title}
⏰ ${course.credit_hours} CE Hours (TREC Approved)
${scheduledDate ? `📅 ${scheduledDate} at ${scheduledTime}` : ''}
${scheduledCourse?.venue_name ? `📍 ${scheduledCourse.venue_name}` : ''}

${course.description}

This course is perfect for agents who want to:
• Stay current with industry best practices
• Learn strategies to grow their business
• Network with other professionals
• Earn CE credits efficiently

Space is limited! Register today:
${courseUrl}

Questions? Reply to this email or call us at (281) 900-5687.

Best regards,
The DealLab CE Team

---
DealLab CE - TREC Provider #9980
info@deallabce.com | (281) 900-5687
Unsubscribe: [Unsubscribe Link]`
    },
    sponsors: {
      subject: `Sponsorship Opportunity: ${course.title} - Reach 50+ Texas Agents`,
      body: `Hi [Sponsor Name],

I'm reaching out with an exclusive sponsorship opportunity that puts your brand in front of motivated Texas real estate professionals.

🎯 THE OPPORTUNITY:
${course.title}
${scheduledDate ? `📅 ${scheduledDate} at ${scheduledTime}` : '📅 Dates TBD'}
👥 Expected Attendance: 50+ Licensed Agents

📋 COURSE OVERVIEW:
${course.description}

💼 SPONSORSHIP BENEFITS:
• Your logo displayed on all course materials
• 5-minute presentation to introduce your services
• Access to attendee contact list (with consent)
• Social media promotion to our network
• Inclusion in post-event email to attendees

📊 AUDIENCE PROFILE:
• Licensed Texas Real Estate Agents & Brokers
• Mix of new and experienced professionals
• Active professionals seeking to grow their business
• High engagement rate (CE requirement = motivated audience)

💰 SPONSORSHIP INVESTMENT:
• Gold Sponsor: $500 (includes all benefits above)
• Silver Sponsor: $250 (logo placement + mentions)
• Bronze Sponsor: $100 (logo placement)

Limited to 4 sponsors per course to ensure quality exposure.

Interested? Let's schedule a quick call to discuss how we can help you reach your ideal clients.

Reply to this email or call me at (281) 900-5687.

Best regards,
[Your Name]
DealLab CE

---
DealLab CE - TREC Provider #9980
info@deallabce.com | (281) 900-5687`
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const openShareLink = (platform) => {
    let shareUrl = '';
    const encodedUrl = encodeURIComponent(courseUrl);
    const encodedText = encodeURIComponent(socialContent[platform].substring(0, 500));

    switch (platform) {
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        break;
      case 'instagram':
        // Instagram doesn't have direct share - copy to clipboard instead
        copyToClipboard(socialContent.instagram, 'instagram');
        toast.info('Content copied! Paste in Instagram app.');
        return;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  // AI Content Generation
  const generateAIContent = async (contentType, platform = null) => {
    const key = platform ? `${contentType}_${platform}` : contentType;
    setGeneratingContent(prev => ({ ...prev, [key]: true }));
    
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_URL}/marketing/generate`, {
        course_id: course.id,
        scheduled_course_id: scheduledCourse?.id || null,
        content_type: contentType,
        platform: platform,
        custom_prompt: customPrompt || null
      }, { headers });
      
      setAiContent(prev => ({ ...prev, [key]: response.data.content }));
      toast.success('AI content generated!');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGeneratingContent(prev => ({ ...prev, [key]: false }));
    }
  };

  // Get content (AI-generated if available, otherwise default)
  const getContent = (key, defaultContent) => {
    return aiContent[key] || defaultContent;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Share2 className="w-5 h-5 text-[#1E3A5F]" />
            Share & Market Course
          </DialogTitle>
          <DialogDescription className="sr-only">
            Share this course on social media, create an Eventbrite event, or use email templates
          </DialogDescription>
        </DialogHeader>

        <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <h3 className="font-semibold text-[#1E3A5F]">{course.title}</h3>
          <p className="text-sm text-slate-600">{course.credit_hours} CE Hours • {course.category}</p>
          {scheduledDate && (
            <p className="text-sm text-sky-600 mt-1">📅 {scheduledDate} at {scheduledTime}</p>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="social" className="flex items-center gap-1.5">
              <Share2 className="w-4 h-4" />
              Social Media
            </TabsTrigger>
            <TabsTrigger value="eventbrite" className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              Eventbrite
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-1.5">
              <Mail className="w-4 h-4" />
              Email Templates
            </TabsTrigger>
          </TabsList>

          {/* Social Media Tab */}
          <TabsContent value="social" className="space-y-4">
            {/* AI Generation Banner */}
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Wand2 className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold">AI-Powered Content</h4>
                  <p className="text-sm text-white/80">Generate custom marketing content with AI</p>
                </div>
              </div>
              <div className="mt-3">
                <Input
                  placeholder="Optional: Add custom instructions (e.g., 'Focus on new agents', 'Mention early bird discount')"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-auto py-4 border-[#0077B5] text-[#0077B5] hover:bg-[#0077B5]/10"
                onClick={() => openShareLink('linkedin')}
                data-testid="share-linkedin"
              >
                <Linkedin className="w-6 h-6" />
                <span className="text-xs">LinkedIn</span>
              </Button>
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-auto py-4 border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2]/10"
                onClick={() => openShareLink('facebook')}
                data-testid="share-facebook"
              >
                <Facebook className="w-6 h-6" />
                <span className="text-xs">Facebook</span>
              </Button>
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-auto py-4 border-[#E4405F] text-[#E4405F] hover:bg-[#E4405F]/10"
                onClick={() => openShareLink('instagram')}
                data-testid="share-instagram"
              >
                <Instagram className="w-6 h-6" />
                <span className="text-xs">Instagram</span>
              </Button>
            </div>

            {/* LinkedIn Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-[#0077B5]" />
                  LinkedIn Post
                </label>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateAIContent('social_media', 'linkedin')}
                    disabled={generatingContent['social_media_linkedin']}
                    className="h-8 text-violet-600 border-violet-300 hover:bg-violet-50"
                    data-testid="ai-generate-linkedin"
                  >
                    {generatingContent['social_media_linkedin'] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3 mr-1" />
                        AI
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(getContent('social_media_linkedin', socialContent.linkedin), 'linkedin')}
                    className="h-8"
                  >
                    {copiedField === 'linkedin' ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              <Textarea
                value={getContent('social_media_linkedin', socialContent.linkedin)}
                readOnly
                className="h-40 text-sm font-mono resize-none"
              />
            </div>

            {/* Facebook Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-[#1877F2]" />
                  Facebook Post
                </label>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateAIContent('social_media', 'facebook')}
                    disabled={generatingContent['social_media_facebook']}
                    className="h-8 text-violet-600 border-violet-300 hover:bg-violet-50"
                    data-testid="ai-generate-facebook"
                  >
                    {generatingContent['social_media_facebook'] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3 mr-1" />
                        AI
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(getContent('social_media_facebook', socialContent.facebook), 'facebook')}
                    className="h-8"
                  >
                    {copiedField === 'facebook' ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              <Textarea
                value={getContent('social_media_facebook', socialContent.facebook)}
                readOnly
                className="h-32 text-sm font-mono resize-none"
              />
            </div>

            {/* Instagram Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-[#E4405F]" />
                  Instagram Caption
                </label>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateAIContent('social_media', 'instagram')}
                    disabled={generatingContent['social_media_instagram']}
                    className="h-8 text-violet-600 border-violet-300 hover:bg-violet-50"
                    data-testid="ai-generate-instagram"
                  >
                    {generatingContent['social_media_instagram'] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3 mr-1" />
                        AI
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(getContent('social_media_instagram', socialContent.instagram), 'instagram-text')}
                    className="h-8"
                  >
                    {copiedField === 'instagram-text' ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              <Textarea
                value={getContent('social_media_instagram', socialContent.instagram)}
                readOnly
                className="h-32 text-sm font-mono resize-none"
              />
            </div>
          </TabsContent>

          {/* Eventbrite Tab */}
          <TabsContent value="eventbrite" className="space-y-4">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">Eventbrite Event Template</span>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => window.open('https://www.eventbrite.com/create', '_blank')}
                  className="bg-white text-orange-600 hover:bg-orange-50"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Create Event
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {/* Event Title */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Event Title</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(eventbriteTemplate.title, 'eb-title')}
                    className="h-7"
                  >
                    {copiedField === 'eb-title' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <div className="p-2 bg-slate-50 rounded border text-sm">{eventbriteTemplate.title}</div>
              </div>

              {/* Event Description */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Event Description</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(eventbriteTemplate.description, 'eb-desc')}
                    className="h-7"
                  >
                    {copiedField === 'eb-desc' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <Textarea
                  value={eventbriteTemplate.description}
                  readOnly
                  className="h-48 text-sm resize-none"
                />
              </div>

              {/* Event Details Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-700">Organizer</label>
                  <div className="p-2 bg-slate-50 rounded border text-sm">{eventbriteTemplate.organizer}</div>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-700">Date & Time</label>
                  <div className="p-2 bg-slate-50 rounded border text-sm">{eventbriteTemplate.date} at {eventbriteTemplate.time}</div>
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Suggested Tags</label>
                <div className="flex flex-wrap gap-1">
                  {eventbriteTemplate.tags.map((tag, i) => (
                    <span key={i} className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="email" className="space-y-4">
            {/* AI Generation for Emails */}
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white p-3 rounded-lg">
              <div className="flex items-center gap-3">
                <Wand2 className="w-5 h-5" />
                <div className="flex-1">
                  <span className="font-medium">Generate custom emails with AI</span>
                </div>
              </div>
            </div>

            {/* Agent Email */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-sky-50 p-3 flex items-center justify-between border-b">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-sky-600" />
                  <span className="font-semibold text-sky-800">Agent Invitation Email</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateAIContent('email_agent')}
                    disabled={generatingContent['email_agent']}
                    className="border-violet-300 text-violet-600 hover:bg-violet-50"
                    data-testid="ai-generate-agent-email"
                  >
                    {generatingContent['email_agent'] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3 mr-1" />
                        AI Generate
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(getContent('email_agent', `Subject: ${emailTemplates.agents.subject}\n\n${emailTemplates.agents.body}`), 'agent-email')}
                    className="border-sky-300 text-sky-600"
                  >
                    {copiedField === 'agent-email' ? <Check className="w-4 h-4 text-green-500 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    Copy All
                  </Button>
                </div>
              </div>
              <div className="p-3 space-y-2">
                <Textarea
                  value={getContent('email_agent', `Subject: ${emailTemplates.agents.subject}\n\n${emailTemplates.agents.body}`)}
                  readOnly
                  className="h-64 text-sm resize-none font-mono"
                />
              </div>
            </div>

            {/* Sponsor Email */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-emerald-50 p-3 flex items-center justify-between border-b">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-800">Sponsor Outreach Email</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateAIContent('email_sponsor')}
                    disabled={generatingContent['email_sponsor']}
                    className="border-violet-300 text-violet-600 hover:bg-violet-50"
                    data-testid="ai-generate-sponsor-email"
                  >
                    {generatingContent['email_sponsor'] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3 mr-1" />
                        AI Generate
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(getContent('email_sponsor', `Subject: ${emailTemplates.sponsors.subject}\n\n${emailTemplates.sponsors.body}`), 'sponsor-email')}
                    className="border-emerald-300 text-emerald-600"
                  >
                    {copiedField === 'sponsor-email' ? <Check className="w-4 h-4 text-green-500 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    Copy All
                  </Button>
                </div>
              </div>
              <div className="p-3 space-y-2">
                <Textarea
                  value={getContent('email_sponsor', `Subject: ${emailTemplates.sponsors.subject}\n\n${emailTemplates.sponsors.body}`)}
                  readOnly
                  className="h-64 text-sm resize-none font-mono"
                />
              </div>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Personalization Tips:</p>
                  <ul className="list-disc ml-4 mt-1 text-amber-700">
                    <li>Replace [Agent Name] / [Sponsor Name] with the recipient's name</li>
                    <li>Customize the intro paragraph based on your relationship</li>
                    <li>Add any special promotions or early-bird pricing</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Quick Copy Course URL */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <div className="flex-1 p-2 bg-slate-100 rounded border text-sm text-slate-600 truncate">
              {courseUrl}
            </div>
            <Button
              variant="outline"
              onClick={() => copyToClipboard(courseUrl, 'course-url')}
              className="flex-shrink-0"
              data-testid="copy-course-url"
            >
              {copiedField === 'course-url' ? (
                <>
                  <Check className="w-4 h-4 mr-1 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy URL
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
